import React from 'react';

import DropZone from '@components/DropZone';
import { AllowType } from '@components/DropZone/Dropzone.types';
import { FilePreview } from '@components/DropZone/FilePreview';
import classNames from 'classnames';
import ThemedIcon from '@components/ThemedIcon';

type Props = {
  files: Array<File>;
  onFilesSelected: (files: Array<File>) => void;
  allowTypes: Array<AllowType>;
  onFileRemoved: (index: number) => void;
  height?: string;
  description?: string;
  recommendation?: string;
  filePreviewSize?: number;
  type?: 'default' | 'dangerous';
  filesLimit?: number;
  withoutRemoveFiles?: boolean;
  isMockPreview?: boolean;
  disabled?: boolean;
};

export function FileUploader({
  files,
  onFilesSelected,
  allowTypes,
  onFileRemoved,
  height = '88px',
  description,
  recommendation,
  filePreviewSize = 40,
  type = 'default',
  filesLimit = 1,
  withoutRemoveFiles,
  isMockPreview,
  disabled,
}: Props) {
  const renderFiles = () =>
    files.map((file: File, index: number) => {
      const handleRemove = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>
      ) => {
        event.stopPropagation();
        onFileRemoved(index);
      };
      return (
        <FilePreview
          key={file.name + index}
          size={filePreviewSize}
          file={file}
          onRemove={!withoutRemoveFiles ? handleRemove : undefined}
        />
      );
    });

  const renderDropZoneContent = () => {
    const renderFilesContainer = (
      content: React.ReactElement | Array<React.ReactElement>
    ) => (
      <div className='flex h-full flex-row gap-4 overflow-x-auto p-3'>
        {content}
      </div>
    );

    const renderEmptyState = () => (
      <div className='flex h-full flex-col items-center justify-center gap-1 rounded-2xl p-4 text-base font-medium'>
        <div
          className={classNames(
            'flex-col items-center justify-center gap-[0.625rem] text-accent-primary',
            { 'text-status-text-error': type === 'dangerous' }
          )}
        >
          <ThemedIcon
            className='mx-auto mb-4'
            name='attach'
            fill={type === 'dangerous' ? '#F53131' : undefined}
          />
          {description}
        </div>
        {recommendation && (
          <p className='text-center text-sm text-status-text-error'>{recommendation}</p>
        )}
      </div>
    );

    if (Array.isArray(files) && files.length > 0) {
      return renderFilesContainer(renderFiles());
    }

    if (isMockPreview) {
      return renderFilesContainer(<FilePreview file={null} />);
    }

    return renderEmptyState();
  };

  return (
    <DropZone
      width='100%'
      height={height}
      onFilesSelected={onFilesSelected}
      allowTypes={allowTypes}
      type={type}
      limit={filesLimit}
      files={files}
      disabled={isMockPreview || files.length >= filesLimit || disabled}
    >
      {renderDropZoneContent()}
    </DropZone>
  );
}
